import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { CreditCard, Loader2 } from "lucide-react";
import { downloadReceipt } from "@/lib/receipt";

const CARD_STACK = "https://images.pexels.com/photos/164501/pexels-photo-164501.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const PRESETS = [25, 50, 100, 250, 500, 1000];

export default function Deposit() {
  const { user, refreshBalance } = useAuth();
  const isLiveUser = user?.banking_mode === "live";
  const [mode, setMode] = useState(isLiveUser ? "live" : (user?.banking_mode || "demo"));
  const [amount, setAmount] = useState("100");
  const [paypalClientId, setPaypalClientId] = useState("");
  const [pendingTxId, setPendingTxId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/config/paypal").then(({ data }) => setPaypalClientId(data.client_id || ""));
  }, []);

  // Force live-account users to live mode if they somehow flip state.
  useEffect(() => {
    if (isLiveUser && mode !== "live") setMode("live");
  }, [isLiveUser, mode]);

  const value = Math.max(0, Number(amount) || 0);

  const handleDemoDeposit = async () => {
    if (value <= 0) { toast.error("Enter an amount greater than zero."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/wallet/deposit/paypal/create", { amount: value, mode: "demo", currency: "USD" });
      await refreshBalance();
      toast.success(`Deposited $${value.toFixed(2)} to your demo wallet.`);
      if (data?.transaction_id) downloadReceipt(data.transaction_id, { silent: true });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <AppLayout>
      <div className="max-w-4xl">
        <div className="text-eyebrow text-[#4F4D45]">Move money in</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Deposit funds</h1>
        <p className="text-[#4F4D45] mt-2 max-w-xl">Top up your wallet from any external bank card via PayPal. Demo mode is sandboxed — funds appear instantly.</p>

        <div className="mt-8 grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 fv-card p-6 md:p-8">
            <Tabs value={mode} onValueChange={setMode} data-testid="deposit-mode-tabs">
              <TabsList className={`bg-[#E3DECF] ${isLiveUser ? "hidden" : ""}`}>
                <TabsTrigger value="demo" data-testid="deposit-tab-demo">Demo</TabsTrigger>
                <TabsTrigger value="live" data-testid="deposit-tab-live">Live (PayPal)</TabsTrigger>
              </TabsList>
              {isLiveUser && (
                <div className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-[#1B3526]/10 text-[#1B3526] font-medium" data-testid="deposit-live-only">
                  Live account · real deposits via PayPal
                </div>
              )}
              <TabsContent value="demo" className="mt-6">
                <p className="text-sm text-[#4F4D45]">Demo deposits use simulated funds — perfect for trying the flow.</p>
              </TabsContent>
              <TabsContent value="live" className="mt-6">
                <p className="text-sm text-[#4F4D45]">Live deposits go through PayPal Checkout. You can pay with any external bank card.</p>
              </TabsContent>
            </Tabs>

            <div className="mt-8">
              <Label className="text-eyebrow text-[#4F4D45]">Amount (USD)</Label>
              <div className="relative mt-1">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#4F4D45]">$</span>
                <Input
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                  className="pl-8 text-3xl font-display font-bold h-16"
                  data-testid="deposit-amount-input"
                />
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {PRESETS.map((p) => (
                  <button
                    key={p}
                    onClick={() => setAmount(String(p))}
                    data-testid={`deposit-preset-${p}`}
                    className="px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.04] text-sm text-[#F0ECE0] hover:border-[#9DCBAD] hover:text-[#9DCBAD] hover:bg-white/[0.08] transition-colors"
                  >
                    ${p}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8 border-t border-[#D2CDBE] pt-6">
              {mode === "demo" ? (
                <Button onClick={handleDemoDeposit} disabled={loading || value <= 0} className="bg-[#1B3526] hover:bg-[#0F2517]" data-testid="deposit-demo-button">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : `Deposit $${value.toFixed(2)} (Demo)`}
                </Button>
              ) : !paypalClientId ? (
                <div className="text-sm text-[#992F2F]">PayPal is not configured.</div>
              ) : value <= 0 ? (
                <div className="text-sm text-[#4F4D45]">Enter an amount above to continue.</div>
              ) : (
                <PayPalScriptProvider
                  options={{
                    clientId: paypalClientId,
                    currency: "USD",
                    intent: "capture",
                  }}
                >
                  <PayPalButtons
                    style={{ layout: "vertical", color: "blue", shape: "rect", label: "pay" }}
                    forceReRender={[value]}
                    createOrder={async () => {
                      try {
                        const { data } = await api.post("/wallet/deposit/paypal/create", {
                          amount: value,
                          mode: "live",
                          currency: "USD",
                        });
                        setPendingTxId(data.transaction_id);
                        return data.order_id;
                      } catch (e) {
                        toast.error(formatApiError(e));
                        throw e;
                      }
                    }}
                    onApprove={async (data) => {
                      try {
                        const { data: result } = await api.post("/wallet/deposit/paypal/capture", {
                          order_id: data.orderID,
                          transaction_id: pendingTxId,
                        });
                        await refreshBalance();
                        if (result.promo_credit) {
                          toast.success(`Deposited $${value.toFixed(2)} · promo ${result.promo_credit.code} added +$${result.promo_credit.amount.toFixed(2)}`);
                        } else {
                          toast.success(`Deposited $${value.toFixed(2)} via PayPal.`);
                        }
                        if (pendingTxId) downloadReceipt(pendingTxId, { silent: true });
                      } catch (e) { toast.error(formatApiError(e)); }
                    }}
                    onError={(err) => {
                      console.error(err);
                      toast.error("PayPal checkout failed. Please try again.");
                    }}
                  />
                </PayPalScriptProvider>
              )}
            </div>
          </div>

          <aside className="lg:col-span-2">
            <div className="fv-card p-0 overflow-hidden">
              <img src={CARD_STACK} alt="Cards" className="w-full h-44 object-cover" />
              <div className="p-6">
                <CreditCard className="w-6 h-6 text-[#A85943] mb-2" strokeWidth={1.5} />
                <h3 className="font-display text-xl font-medium">Bank cards only</h3>
                <p className="text-sm text-[#4F4D45] mt-2 leading-relaxed">
                  Per our policy, deposits accept only external bank cards. PayPal handles the card processing — we never see or store your card number.
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </AppLayout>
  );
}
